import axios from "axios";
import { LOG_OUT, REFRESH_TOKEN } from "../redux/actionTypes";
import { store } from "../redux/store";

export const API = axios.create({
  baseURL: `${process.env.REACT_APP_MS_BASE_URL}/`,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// REQUEST INTERCEPTOR — attach Bearer token
API.interceptors.request.use((req) => {
  const raw = localStorage.getItem("user");
  if (raw) {
    req.headers.Authorization = `Bearer ${JSON.parse(raw)}`;
  }
  return req;
});

// RESPONSE INTERCEPTOR — silent token refresh on 401
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return API(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const rawRefresh = localStorage.getItem("refresh_token");
        if (!rawRefresh) throw new Error("No refresh token");

        const { data } = await axios.post(
          `${process.env.REACT_APP_MS_BASE_URL}/auth/refresh/`,
          { refresh: JSON.parse(rawRefresh) }
        );

        const newAccessToken = data.access;
        const newRefreshToken = data.refresh;
        localStorage.setItem("user", JSON.stringify(newAccessToken));
        if (newRefreshToken) {
          localStorage.setItem("refresh_token", JSON.stringify(newRefreshToken));
        }
        store.dispatch({ type: REFRESH_TOKEN, payload: { access: newAccessToken, refresh: newRefreshToken } });
        API.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return API(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.dispatch({ type: LOG_OUT });
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ======================== Auth ===========================
export const googleAuth = (credential) =>
  API.post("auth/google/", { credential });

export const getMyProfile = () => API.get("auth/me/");

// ======================== Notes ==========================
export const getNotes = () => API.get("notes/");

export const createNote = (data) => API.post("notes/", data);

export const getNote = (id) => API.get(`notes/${id}/`);

export const updateNote = (id, data) => API.patch(`notes/${id}/`, data);

export const deleteNote = (id) => API.delete(`notes/${id}/`);

// ======================== Billing ========================
export const getBillingPlans = () => API.get("billing/plans/");

export const getSubscription = () => API.get("billing/subscription/");

// LemonSqueezy flow
export const createCheckout = (planId, billingPeriod, successUrl) =>
  API.post("billing/checkout/", { plan_id: planId, billing_period: billingPeriod, success_url: successUrl });

export const confirmOrder = (internalOrderId, lsOrderId) =>
  API.post("billing/confirm/", { internal_order_id: internalOrderId, ls_order_id: lsOrderId });

export const cancelSubscription = () => API.post("billing/cancel/");

// Razorpay legacy (kept for switchability)
export const createPaymentOrder = (planId, billingPeriod) =>
  API.post("billing/orders/", { plan_id: planId, billing_period: billingPeriod });

export const verifyPayment = (payload) => API.post("billing/verify/", payload);

export const getBillingHistory = () => API.get("billing/history/");

